/**
 * Who a tab is for.
 *
 * A waiter names the guest — found by name or number, or added on the spot
 * with just a name — and from then on the tab carries what the kitchen and
 * the floor must not forget: what they are allergic to, what they do not
 * eat, and anything the restaurant wrote down about them. That strip sits at
 * the top of the tab in warning colours, above the dishes, because the
 * moment it matters is when the next dish is being rung in.
 *
 * Asking whether they would like to hear from the restaurant is here too,
 * with a plain yes and a plain no and nothing ticked in advance: nobody is
 * signed up by a default.
 */

import { useState } from 'react';
import {
  ALLERGENS,
  ALLERGEN_LABELS,
  CONSENT_CHANNEL_LABELS,
  consentOn,
  describeConsent,
  displayLocale,
  useAddGuest,
  useAttachGuest,
  useDetachGuest,
  useEntitlements,
  useGuest,
  useGuestHistory,
  useGuests,
  usePermission,
  useRecordConsent,
  type Allergen,
  type ConsentChannel,
  type Guest,
  type Order,
} from '@restaurant/api-client';
import {
  ActionError,
  Badge,
  Button,
  Empty,
  ErrorNotice,
  Field,
  Input,
  Loading,
  Sheet,
} from '@restaurant/ui';

import { usePointsOnTill } from './guest-access';
import { PointsBadge, PointsSheet } from './points';
import './guest.css';

function allergenLabel(code: string): string {
  return ALLERGEN_LABELS[code as Allergen] ?? code;
}

/** A business date as the site writes it. Built from its parts, never parsed as UTC. */
function day(value: string): string {
  const [year, month, date] = value.split('-').map(Number);
  return new Date(year ?? 1970, (month ?? 1) - 1, date ?? 1).toLocaleDateString(
    displayLocale(),
    {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    },
  );
}

/**
 * The guest on this tab, at the top of it. Allergies first and loudest.
 * Tapping it opens the guest.
 */
export function GuestStrip({ order, onOpen }: { order: Order; onOpen: () => void }) {
  const guest = useGuest(order.customer_id);
  const pointsOn = usePointsOnTill();
  if (!order.customer_id) return null;
  if (guest.isPending) return null;
  if (guest.isError) {
    return (
      <button type="button" className="gs gs--quiet" onClick={onOpen}>
        <span className="gs__name">A guest is on this tab</span>
      </button>
    );
  }
  const g = guest.data;
  const warn = g.allergens.length > 0 || Boolean(g.dietary);
  return (
    <button
      type="button"
      className={`gs${warn ? ' gs--warn' : ''}`}
      onClick={onOpen}
      aria-label={`${g.name}. Open the guest`}
    >
      <span className="gs__name">
        {g.name}
        {pointsOn && (
          <>
            {' · '}
            <PointsBadge customerId={g.id} />
          </>
        )}
      </span>
      {g.allergens.length > 0 && (
        <span className="gs__allergy" role="note">
          Allergic to {g.allergens.map(allergenLabel).join(', ')}
        </span>
      )}
      {g.dietary && <span className="gs__line">{g.dietary}</span>}
      {g.notes && <span className="gs__line gs__line--soft">{g.notes}</span>}
    </button>
  );
}

export function GuestSheet({ order, onDone }: { order: Order; onDone: () => void }) {
  const [choosing, setChoosing] = useState(order.customer_id === null);
  const [spending, setSpending] = useState(false);

  if (spending && order.customer_id) {
    return <PointsSheet order={order} onDone={onDone} />;
  }
  if (!choosing && order.customer_id) {
    return (
      <OnTheTab
        order={order}
        customerId={order.customer_id}
        onChoose={() => setChoosing(true)}
        onPoints={() => setSpending(true)}
        onDone={onDone}
      />
    );
  }
  return <FindGuest order={order} onDone={onDone} />;
}

function OnTheTab({
  order,
  customerId,
  onChoose,
  onPoints,
  onDone,
}: {
  order: Order;
  customerId: string;
  onChoose: () => void;
  onPoints: () => void;
  onDone: () => void;
}) {
  const guest = useGuest(customerId);
  const history = useGuestHistory(customerId);
  const detach = useDetachGuest();
  const mayChange = usePermission('orders.order.update');
  const pointsOn = usePointsOnTill();
  const mayRedeem = usePermission('customers.loyalty.redeem');

  return (
    <Sheet
      title={guest.data ? guest.data.name : `Guest on #${order.number}`}
      onDismiss={onDone}
      actions={
        mayChange ? (
          <>
            <Button size="lg" block onClick={onChoose}>
              Someone else
            </Button>
            <Button
              size="lg"
              block
              loading={detach.isPending}
              onClick={() => detach.mutate({ orderId: order.id }, { onSuccess: onDone })}
            >
              Take off this tab
            </Button>
          </>
        ) : undefined
      }
    >
      {guest.isPending ? (
        <Loading label="Finding the guest" />
      ) : guest.isError ? (
        <ErrorNotice message={guest.error.message} />
      ) : (
        <>
          <GuestFacts guest={guest.data} />
          {pointsOn && (
            <div className="gc__row">
              <span className="gc__channel">Points</span>
              <span className="gc__now">
                <PointsBadge customerId={customerId} />
              </span>
              {mayRedeem && order.state === 'open' && (
                <span className="gc__acts">
                  <Button size="sm" onClick={onPoints}>
                    Use points
                  </Button>
                </span>
              )}
            </div>
          )}
          {history.data && history.data.visits > 0 && (
            <p className="os__sheet-note">
              {history.data.visits} visit{history.data.visits === 1 ? '' : 's'} before
              {history.data.last_visit ? `, last on ${day(history.data.last_visit)}` : ''}
              {history.data.favourites.length > 0
                ? `. Usually has ${history.data.favourites
                    .slice(0, 3)
                    .map((f) => f.name)
                    .join(', ')}.`
                : '.'}
            </p>
          )}
          <ConsentAsk guest={guest.data} />
        </>
      )}
      <ActionError
        error={detach.error}
        action="Taking the guest off"
        title="Still on the tab"
      />
    </Sheet>
  );
}

function GuestFacts({ guest }: { guest: Guest }) {
  return (
    <div className="gf">
      {guest.allergens.length > 0 ? (
        <div className="gf__allergies" role="note">
          <span className="gf__label">Allergies</span>
          <span className="gf__chips">
            {guest.allergens.map((code) => (
              <Badge key={code} tone="danger">
                {allergenLabel(code)}
              </Badge>
            ))}
          </span>
        </div>
      ) : (
        <p className="gf__none">No allergies on file.</p>
      )}
      {guest.dietary && (
        <p className="gf__row">
          <span className="gf__label">Diet</span> {guest.dietary}
        </p>
      )}
      {guest.notes && (
        <p className="gf__row">
          <span className="gf__label">Notes</span> {guest.notes}
        </p>
      )}
      {(guest.phone || guest.email) && (
        <p className="gf__row gf__row--soft">
          {[guest.phone, guest.email].filter(Boolean).join(' · ')}
        </p>
      )}
      {guest.tags.length > 0 && (
        <span className="gf__chips">
          {guest.tags.map((tag) => (
            <Badge key={tag}>{tag}</Badge>
          ))}
        </span>
      )}
    </div>
  );
}

/**
 * "Would you like to hear from us?" — asked, not assumed. Each channel shows
 * what the guest said last and takes a new answer; a yes needs an address to
 * send to, and the server says so if there is none.
 */
function ConsentAsk({ guest }: { guest: Guest }) {
  const entitlements = useEntitlements();
  const mayWrite = usePermission('customers.guest.write');
  const record = useRecordConsent();
  if (!entitlements.has('customers.consent') || !mayWrite) return null;

  const channels: ConsentChannel[] = ['email', 'sms'];
  return (
    <div className="gc">
      <p className="gf__label">Happy to hear from the restaurant?</p>
      {channels.map((channel) => {
        const state = consentOn(guest, channel);
        return (
          <div key={channel} className="gc__row">
            <span className="gc__channel">{CONSENT_CHANNEL_LABELS[channel]}</span>
            <span className="gc__now">{describeConsent(state)}</span>
            <span className="gc__acts">
              <Button
                size="sm"
                variant={state.given === true ? 'primary' : 'secondary'}
                disabled={record.isPending || state.given === true}
                onClick={() =>
                  record.mutate({ id: guest.id, channel, given: true, source: 'till' })
                }
              >
                Yes
              </Button>
              <Button
                size="sm"
                variant={state.given === false ? 'primary' : 'secondary'}
                disabled={record.isPending || state.given === false}
                onClick={() =>
                  record.mutate({ id: guest.id, channel, given: false, source: 'till' })
                }
              >
                No
              </Button>
            </span>
          </div>
        );
      })}
      <ActionError error={record.error} action="Recording the answer" title="Not recorded" />
    </div>
  );
}

function FindGuest({ order, onDone }: { order: Order; onDone: () => void }) {
  const [query, setQuery] = useState('');
  const [adding, setAdding] = useState(false);
  const found = useGuests(query, { enabled: query.trim().length >= 2, limit: 12 });
  const attach = useAttachGuest();
  const mayAdd = usePermission('customers.guest.write');

  if (adding) {
    return (
      <QuickAdd order={order} typed={query} onBack={() => setAdding(false)} onDone={onDone} />
    );
  }

  return (
    <Sheet
      title={`Who is #${order.number} for?`}
      onDismiss={onDone}
      actions={
        mayAdd ? (
          <Button size="lg" block onClick={() => setAdding(true)}>
            New guest
          </Button>
        ) : undefined
      }
    >
      <Field label="Name, phone or email">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
          autoComplete="off"
          placeholder="Maria, or 912 345"
        />
      </Field>
      {query.trim().length < 2 ? (
        <p className="os__sheet-note">Two letters or digits are enough to start.</p>
      ) : found.isPending ? (
        <Loading label="Looking" />
      ) : found.isError ? (
        <ErrorNotice message={found.error.message} />
      ) : found.data.length === 0 ? (
        <Empty
          title="Nobody by that"
          description={mayAdd ? 'Add them as a new guest.' : 'Ask a manager to add them.'}
        />
      ) : (
        <ul className="gl">
          {found.data.map((guest) => (
            <li key={guest.id}>
              <button
                type="button"
                className="gl__row"
                disabled={attach.isPending}
                onClick={() =>
                  attach.mutate(
                    { orderId: order.id, customerId: guest.id },
                    { onSuccess: onDone },
                  )
                }
              >
                <span className="gl__name">{guest.name}</span>
                <span className="gl__contact">
                  {[guest.phone, guest.email].filter(Boolean).join(' · ')}
                </span>
                {guest.allergens.length > 0 && (
                  <span className="gl__allergy">
                    {guest.allergens.map(allergenLabel).join(', ')}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
      <ActionError error={attach.error} action="Naming the guest" title="Not on the tab" />
    </Sheet>
  );
}

/** The fewest questions that make a guest worth having: a name, and what they cannot eat. */
function QuickAdd({
  order,
  typed,
  onBack,
  onDone,
}: {
  order: Order;
  typed: string;
  onBack: () => void;
  onDone: () => void;
}) {
  const looksLikeContact = /[@\d]/.test(typed);
  const [name, setName] = useState(looksLikeContact ? '' : typed.trim());
  const [phone, setPhone] = useState(
    looksLikeContact && !typed.includes('@') ? typed.trim() : '',
  );
  const [email, setEmail] = useState(typed.includes('@') ? typed.trim() : '');
  const [allergens, setAllergens] = useState<string[]>([]);
  const [dietary, setDietary] = useState('');
  const add = useAddGuest();
  const attach = useAttachGuest();

  function toggle(code: string) {
    setAllergens((now) =>
      now.includes(code) ? now.filter((c) => c !== code) : [...now, code],
    );
  }

  function save() {
    add.mutate(
      {
        name: name.trim(),
        phone: phone.trim() || null,
        email: email.trim() || null,
        allergens,
        dietary: dietary.trim() || null,
      },
      {
        onSuccess: (guest) =>
          attach.mutate({ orderId: order.id, customerId: guest.id }, { onSuccess: onDone }),
      },
    );
  }

  return (
    <Sheet
      title="New guest"
      onDismiss={onDone}
      actions={
        <>
          <Button size="lg" block onClick={onBack}>
            Back to search
          </Button>
          <Button
            variant="primary"
            size="lg"
            block
            disabled={name.trim() === ''}
            loading={add.isPending || attach.isPending}
            onClick={save}
          >
            Add and put on #{order.number}
          </Button>
        </>
      }
    >
      <Field label="Name">
        <Input
          value={name}
          maxLength={120}
          onChange={(e) => setName(e.target.value)}
          autoFocus
        />
      </Field>
      <Field label="Phone" hint="Optional.">
        <Input
          value={phone}
          inputMode="tel"
          maxLength={40}
          onChange={(e) => setPhone(e.target.value)}
        />
      </Field>
      <Field label="Email" hint="Optional.">
        <Input
          value={email}
          type="email"
          maxLength={320}
          onChange={(e) => setEmail(e.target.value)}
        />
      </Field>
      <p className="gf__label">Allergies</p>
      <div className="ga" role="group" aria-label="Allergies">
        {ALLERGENS.map((code) => (
          <button
            key={code}
            type="button"
            className={`ga__chip${allergens.includes(code) ? ' is-on' : ''}`}
            aria-pressed={allergens.includes(code)}
            onClick={() => toggle(code)}
          >
            {ALLERGEN_LABELS[code]}
          </button>
        ))}
      </div>
      <Field label="Anything else they do not eat" hint="Vegan, no pork, coeliac.">
        <Input value={dietary} maxLength={500} onChange={(e) => setDietary(e.target.value)} />
      </Field>
      <ActionError error={add.error} action="Adding the guest" title="Not added" />
      <ActionError
        error={attach.error}
        action="Naming the guest"
        title="Added, not on the tab"
      />
    </Sheet>
  );
}
